import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getUpdateEmspUserOcpiTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/update-emsp-user-ocpi-token.form-default-values';
import { UpdateEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/update-emsp-user-ocpi-token.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoUpdateOcpiTokenForEmspUserMutation } from '@app/core/service/emsp-bo-emsp-user.service';

import {
  UpdateEmspBoEmspUserOcpiTokenContextProvider,
  useEmspBoEmspUserOcpiTokenContext,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserOcpiTokenUpdateFormController } from './emsp-bo-emsp-user-ocpi-token-update-form/emsp-bo-emsp-user-ocpi-token-update-form.controller';

// /////////////////////////////////////////////////////////////////////////////

interface EmspBoEmspUserOcpiTokenUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoEmspUserOcpiTokenUpdateController = ({
  onCancel,
  onSuccess,
}: EmspBoEmspUserOcpiTokenUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ////////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateEmspUserOcpiToken] =
    useEmspBoUpdateOcpiTokenForEmspUserMutation();
  const ocpiToken = useEmspBoEmspUserOcpiTokenContext();
  const [multiStepFormState, setMultiStepFormState] =
    useState<MultiStepFormState>(MultiStepFormState.Default);

  // HANDLERS ///////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<UpdateEmspUserOcpiTokenModel> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-emsp-user-ocpi-token-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    UpdateEmspUserOcpiTokenModel
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    updateEmspUserOcpiToken(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-emsp-user-ocpi-token-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-emsp-user-ocpi-token-update.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER /////////////////////////////////////////////////////////////////////

  const data = getUpdateEmspUserOcpiTokenDefaultValues(ocpiToken);

  return (
    <UpdateEmspBoEmspUserOcpiTokenContextProvider data={data}>
      <EmspBoEmspUserOcpiTokenUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateEmspBoEmspUserOcpiTokenContextProvider>
  );
};
